import { Directive, ElementRef, NgZone, OnDestroy, OnInit } from '@angular/core';

/**
 * Lets a plain mouse wheel scroll a horizontal strip sideways, so a desktop
 * user doesn't have to hold Shift (or find the scrollbar) to see the rest of
 * a row of offers or time slots. Pairs with `uiDragScroll` on the same
 * element; touch and trackpad horizontal swipes are untouched.
 *
 * Usage: <div class="ui-hscroll" uiDragScroll uiWheelScroll>…</div>
 *
 * Only vertical wheel movement is redirected, and only while the strip can
 * still move in that direction — at either end the event is left alone so
 * the page keeps scrolling normally. Trackpads that already send horizontal
 * deltas are passed through as well.
 */
@Directive({
  selector: '[uiWheelScroll]',
})
export class UiWheelScrollDirective implements OnInit, OnDestroy {
  constructor(private el: ElementRef<HTMLElement>, private zone: NgZone) {}

  ngOnInit(): void {
    // scrollLeft is set directly; nothing here needs change detection
    this.zone.runOutsideAngular(() => {
      this.el.nativeElement.addEventListener('wheel', this.onWheel, {
        passive: false,
      });
    });
  }

  ngOnDestroy(): void {
    this.el.nativeElement.removeEventListener('wheel', this.onWheel);
  }

  private onWheel = (event: WheelEvent): void => {
    const node = this.el.nativeElement;
    const maxScroll = node.scrollWidth - node.clientWidth;
    if (maxScroll <= 0) return; // nothing hidden, nothing to do

    // the browser already handles horizontal deltas (trackpads, shift+wheel)
    if (Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;

    // line / page deltas → approximate pixels
    let delta = event.deltaY;
    if (event.deltaMode === 1) delta *= 16;
    else if (event.deltaMode === 2) delta *= node.clientWidth;

    const atStart = node.scrollLeft <= 0 && delta < 0;
    const atEnd = node.scrollLeft >= maxScroll - 1 && delta > 0;
    if (atStart || atEnd) return; // let the page scroll past the strip

    event.preventDefault();
    node.scrollLeft += delta;
  };
}
